/**
 * API Request/Response schemas for Textborne frontend
 */

import { z } from 'zod';
import {
  ContentRatingSchema,
  ReasoningEffortSchema,
  ScaffoldPhaseSchema,
  PhaseGroupSchema,
  TraceTriggerTypeSchema,
} from '../enums';
import {
  UserSchema,
  WorldSchema,
  WorldSeedSchema,
  ScaffoldedCharacterSchema,
  ScaffoldedFactSchema,
  CharacterSchema,
  MessageSchema,
} from '../entities';
import {
  CompanionProfileSchema,
  CompanionSummarySchema,
  CompanionMemorySchema,
  CompanionContactSchema,
} from '../entities';

// ============================================
// Authentication
// ============================================

export const AuthResponseSchema = z.object({
  user: UserSchema,
  token: z.string(),
});

export type AuthResponse = z.infer<typeof AuthResponseSchema>;

// ============================================
// World Creation
// ============================================

export const CreateWorldRequestSchema = z.object({
  title: z.string(),
  prompt: z.string().optional(),
  genre: z.string().optional(),
  tone: z.string().optional(),
  simulationType: z.enum(['narrative', 'companion']).optional(),
  themes: z.array(z.string()).optional(),
  contentRating: ContentRatingSchema.optional(),
  allowNsfw: z.boolean().optional(),
  worldRules: z.array(z.string()).optional(),
  provider: z.string().optional(),
  model: z.string().optional(),
  reasoningEffort: ReasoningEffortSchema.nullable().optional(),
  companionName: z.string().optional(),
  userPreferredName: z.string().optional(),
  pronouns: z.string().optional(),
  apparentAge: z.string().optional(),
  userDescription: z.string().optional(),
  role: z.enum(['friend', 'mentor', 'coach', 'confidant', 'partner', 'custom']).optional(),
  communicationMedium: z.enum(['text_message', 'chat_app', 'social_dm', 'voice_call', 'video_call', 'in_person', 'letters', 'journal']).optional(),
  boundaries: z.object({
    allowRoleplay: z.boolean().optional(),
    allowRomance: z.boolean().optional(),
    safetyMode: z.enum(['standard', 'permissive']).optional(),
    topicsToAvoid: z.array(z.string()).optional(),
  }).optional(),
  settingDefinition: z.string().optional(),
  initialSituation: z.string().optional(),
  characters: z.array(ScaffoldedCharacterSchema).optional(),
  initialFacts: z.array(ScaffoldedFactSchema).optional(),
  playerCharacterName: z.string().nullable().optional(),
  scaffoldingTraceId: z.string().optional(),
  scaffoldingAgentUsage: z.array(z.object({
    agentId: z.string(),
    provider: z.string(),
    model: z.string(),
    promptTokens: z.number(),
    completionTokens: z.number(),
    totalTokens: z.number(),
    latencyMs: z.number(),
  })).optional(),
});

export type CreateWorldRequest = z.infer<typeof CreateWorldRequestSchema>;

export const WorldScaffoldingResultSchema = z.object({
  settingDefinition: z.string(),
  genre: z.string(),
  tone: z.string(),
  themes: z.array(z.string()),
  worldRules: z.array(z.string()),
  initialSituation: z.string(),
  characters: z.array(ScaffoldedCharacterSchema),
  initialFacts: z.array(ScaffoldedFactSchema),
  scaffoldingTraceId: z.string().optional(),
  scaffoldingAgentUsage: z.array(z.object({
    agentId: z.string(),
    provider: z.string(),
    model: z.string(),
    promptTokens: z.number(),
    completionTokens: z.number(),
    totalTokens: z.number(),
    latencyMs: z.number(),
  })).optional(),
});

export type WorldScaffoldingResult = z.infer<typeof WorldScaffoldingResultSchema>;

// ============================================
// Scaffold Progress
// ============================================

export const ScaffoldProgressEventSchema = z.object({
  phase: ScaffoldPhaseSchema,
  message: z.string(),
  progress: z.number(),
  details: z.object({
    characterName: z.string().optional(),
    charactersFound: z.array(z.string()).optional(),
    characterCount: z.number().optional(),
    iteration: z.number().optional(),
    totalIterations: z.number().optional(),
    enrichedFields: z.array(z.string()).optional(),
    genre: z.string().optional(),
    tone: z.string().optional(),
    themes: z.array(z.string()).optional(),
    settingPreview: z.string().optional(),
  }).optional(),
  timestamp: z.number(),
});

export type ScaffoldProgressEvent = z.infer<typeof ScaffoldProgressEventSchema>;

// ============================================
// World Settings
// ============================================

export const UpdateWorldSettingsRequestSchema = z.object({
  title: z.string().optional(),
  provider: z.string().optional(),
  model: z.string().optional(),
  content_rating: ContentRatingSchema.optional(),
  allow_nsfw: z.boolean().optional(),
  reasoning_effort: ReasoningEffortSchema.nullable().optional(),
});

export type UpdateWorldSettingsRequest = z.infer<typeof UpdateWorldSettingsRequestSchema>;

// ============================================
// Models API
// ============================================

export const ModelInfoSchema = z.object({
  id: z.string(),
  provider: z.string(),
  modelId: z.string(),
  displayName: z.string(),
  categories: z.array(z.string()),
  contextWindow: z.number(),
  pricing: z.object({
    input: z.number(),
    output: z.number(),
  }),
  supportsReasoning: z.boolean(),
  supportedReasoningEfforts: z.array(ReasoningEffortSchema).optional(),
  defaultReasoningEffort: ReasoningEffortSchema.optional(),
  reasoningMandatory: z.boolean(),
  avgLatencyMs: z.number(),
});

export type ModelInfo = z.infer<typeof ModelInfoSchema>;

// ============================================
// Admin API
// ============================================

export const AdminStatsSchema = z.object({
  totalUsers: z.number(),
  totalWorlds: z.number(),
  totalMessages: z.number(),
  totalTokensUsed: z.number(),
  totalCostMicros: z.number(),
  totalBilledMicros: z.number(),
  activeWorldsLast24h: z.number(),
  usersLast24h: z.number(),
});

export type AdminStats = z.infer<typeof AdminStatsSchema>;

export const AdminUserSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string().nullable(),
  is_admin: z.union([z.number(), z.boolean()]),
  created_at: z.string(),
  world_count: z.number(),
  message_count: z.number(),
  total_tokens: z.number(),
  total_cost_micros: z.number(),
  total_billed_micros: z.number(),
  total_profit_micros: z.number(),
  balance_micros: z.number(),
  last_active: z.string().nullable(),
  last_logged_in_at: z.string().nullable(),
});

export type AdminUserSummary = z.infer<typeof AdminUserSummarySchema>;

export const AdminUserDetailSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string().nullable(),
  is_admin: z.union([z.number(), z.boolean()]),
  email_verified: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

export const AdminWorldSummarySchema = z.object({
  id: z.string(),
  user_id: z.string(),
  user_email: z.string(),
  title: z.string(),
  status: z.string(),
  genre: z.string().nullable(),
  tone: z.string().nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  message_count: z.number(),
  character_count: z.number(),
  total_tokens: z.number(),
  total_cost_micros: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type AdminWorldSummary = z.infer<typeof AdminWorldSummarySchema>;

export const AdminWorldUsageStatsSchema = z.object({
  request_count: z.number().nullable(),
  total_tokens: z.number().nullable(),
  total_cost_micros: z.number().nullable(),
  avg_latency_ms: z.number().nullable(),
});

export type AdminWorldUsageStats = z.infer<typeof AdminWorldUsageStatsSchema>;

export const AdminWorldInferenceLogSchema = z.object({
  id: z.string(),
  world_id: z.string().nullable(),
  user_id: z.string(),
  request_type: z.string(),
  agent_name: z.string().nullable(),
  provider: z.string(),
  model: z.string(),
  prompt_tokens: z.number().nullable(),
  completion_tokens: z.number().nullable(),
  latency_ms: z.number().nullable(),
  success: z.boolean(),
  error_message: z.string().nullable(),
  created_at: z.string(),
});

export type AdminWorldInferenceLog = z.infer<typeof AdminWorldInferenceLogSchema>;

export const AdminScaffoldingTraceSchema = z.object({
  id: z.string(),
  triggerType: TraceTriggerTypeSchema,
  triggerContent: z.string().nullable(),
  totalCalls: z.number(),
  totalInputTokens: z.number().nullable(),
  totalOutputTokens: z.number().nullable(),
  totalLatencyMs: z.number().nullable(),
  status: z.string(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
});

export type AdminScaffoldingTrace = z.infer<typeof AdminScaffoldingTraceSchema>;

export const AdminWorldDetailSchema = z.object({
  world: WorldSchema,
  seed: WorldSeedSchema.nullable(),
  characters: z.array(CharacterSchema),
  recentMessages: z.array(MessageSchema),
  companionProfile: CompanionProfileSchema.nullable().optional(),
  companionSummary: CompanionSummarySchema.nullable().optional(),
  companionMemories: z.array(CompanionMemorySchema).optional(),
  companionContacts: z.array(CompanionContactSchema).optional(),
  usageStats: AdminWorldUsageStatsSchema.optional(),
  recentInference: z.array(AdminWorldInferenceLogSchema),
  curationLogs: z.array(AdminWorldInferenceLogSchema),
  scaffoldingTrace: AdminScaffoldingTraceSchema.nullable(),
});

export type AdminWorldDetail = z.infer<typeof AdminWorldDetailSchema>;

export const AdminUsageSummarySchema = z.object({
  date: z.string(),
  request_count: z.number(),
  total_prompt_tokens: z.number(),
  total_completion_tokens: z.number(),
  total_tokens: z.number(),
  total_cost_micros: z.number(),
  by_provider: z.record(z.string(), z.object({
    request_count: z.number(),
    tokens: z.number(),
    cost_micros: z.number(),
  })),
  by_request_type: z.record(z.string(), z.object({
    request_count: z.number(),
    tokens: z.number(),
    cost_micros: z.number(),
  })),
});

export type AdminUsageSummary = z.infer<typeof AdminUsageSummarySchema>;

export const AdminInferenceLogSchema = z.object({
  id: z.string(),
  world_id: z.string().nullable(),
  world_title: z.string().nullable(),
  user_email: z.string(),
  request_type: z.string(),
  agent_name: z.string().nullable(),
  provider: z.string(),
  model: z.string(),
  prompt_tokens: z.number().nullable(),
  completion_tokens: z.number().nullable(),
  latency_ms: z.number().nullable(),
  success: z.number(),
  error_message: z.string().nullable(),
  created_at: z.string(),
});

export type AdminInferenceLog = z.infer<typeof AdminInferenceLogSchema>;

export const AdminInferenceLogDetailSchema = AdminInferenceLogSchema.extend({
  system_prompt: z.string().nullable(),
  messages_json: z.string(),
  response_content: z.string().nullable(),
  tool_calls_json: z.string().nullable(),
  temperature: z.number().nullable(),
  max_tokens: z.number().nullable(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
});

export type AdminInferenceLogDetail = z.infer<typeof AdminInferenceLogDetailSchema>;

export const AdminInvitationCodeSchema = z.object({
  id: z.string(),
  code: z.string(),
  maxUses: z.number().nullable(),
  usedCount: z.number().nullable(),
  note: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  isValid: z.boolean(),
  remainingUses: z.number().nullable(),
});

export type AdminInvitationCode = z.infer<typeof AdminInvitationCodeSchema>;

// ============================================
// Tracing API
// ============================================

export const TraceSpanSchema = z.object({
  spanId: z.string(),
  parentSpanId: z.string().nullable(),
  agentName: z.string(),
  depth: z.number(),
  model: z.string(),
  provider: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  latencyMs: z.number(),
  success: z.boolean(),
  errorCode: z.string().optional(),
  startedAt: z.string(),
  systemPrompt: z.string().optional(),
  messagesJson: z.string().optional(),
  responseContent: z.string().optional(),
  phaseGroup: PhaseGroupSchema.optional(),
});

export type TraceSpan = z.infer<typeof TraceSpanSchema>;

export const TraceSummarySchema = z.object({
  id: z.string(),
  worldId: z.string(),
  userId: z.string(),
  triggerType: TraceTriggerTypeSchema,
  triggerContent: z.string().nullable(),
  totalCalls: z.number(),
  totalInputTokens: z.number(),
  totalOutputTokens: z.number(),
  totalLatencyMs: z.number(),
  rootSpanId: z.string().nullable(),
  status: z.enum(['in_progress', 'completed', 'failed']),
  errorMessage: z.string().nullable(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  spans: z.array(TraceSpanSchema).optional(),
});

export type TraceSummary = z.infer<typeof TraceSummarySchema>;

// ============================================
// Scaffold World Request
// ============================================

export const ScaffoldWorldRequestSchema = z.object({
  title: z.string(),
  prompt: z.string(),
  genre: z.string().optional(),
  tone: z.string().optional(),
  themes: z.array(z.string()).optional(),
  contentRating: ContentRatingSchema,
});

export type ScaffoldWorldRequest = z.infer<typeof ScaffoldWorldRequestSchema>;
